import {
  type RefObject,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
} from 'react';

import { type EventListener, EventManager } from './EventManager';
import type { SwimlaneApi, SwimlanePrivateApi } from './types';
import { useSwimlaneApiMethod } from './useSwimlaneApiMethod';

const SYMBOL_API_PRIVATE = Symbol('rm.swimlane_api_private');

let globalId = 1;

function createPrivateAPI(publicApiRef: RefObject<SwimlaneApi>): SwimlanePrivateApi {
  const existingPrivateApi = (publicApiRef.current as never)?.[SYMBOL_API_PRIVATE];
  if (existingPrivateApi) {
    return existingPrivateApi;
  }

  const privateApi = {
    instanceId: globalId,
  } as unknown as SwimlanePrivateApi;

  globalId += 1;

  privateApi.getPublicApi = () => publicApiRef.current;

  privateApi.register = (visibility, members) => {
    Object.keys(members).forEach((memberName) => {
      const method = (members as never)[memberName];

      privateApi[memberName as keyof typeof privateApi] = method;

      if (visibility === 'public') {
        const publicApi = publicApiRef.current;
        publicApi[memberName as keyof typeof publicApi] = method;
      }
    });
  };

  privateApi.register('private', {
    eventManager: new EventManager(),
  });

  return privateApi;
}

function createPublicAPI(privateApiRef: RefObject<SwimlanePrivateApi>): SwimlaneApi {
  return {
    get instanceId() {
      return privateApiRef.current.instanceId;
    },
    [SYMBOL_API_PRIVATE]: privateApiRef.current,
  } as unknown as SwimlaneApi;
}

export function useSwimlaneApiInitialization(
  inputApiRef: RefObject<SwimlaneApi>,
): RefObject<SwimlanePrivateApi> {
  const publicApiRef = useRef<SwimlaneApi>(inputApiRef?.current) as RefObject<SwimlaneApi>;
  const existingPrivateApi = (publicApiRef.current as never)?.[SYMBOL_API_PRIVATE];
  const privateApiRef = useRef<SwimlanePrivateApi>(
    existingPrivateApi,
  ) as RefObject<SwimlanePrivateApi>;

  if (!privateApiRef.current) {
    privateApiRef.current = createPrivateAPI(publicApiRef);
  }

  if (!publicApiRef.current) {
    publicApiRef.current = createPublicAPI(privateApiRef);
  }

  const publishEvent = useCallback<SwimlanePrivateApi['publishEvent']>((...args: unknown[]) => {
    const [name, params, event = {}] = args;

    if ((event as SyntheticEvent)?.isPropagationStopped?.()) {
      return;
    }

    const details = { api: privateApiRef.current?.getPublicApi() };
    privateApiRef.current?.eventManager.emit(name as string, params, event, details);
  }, []);

  const subscribeEvent = useCallback<SwimlaneApi['subscribeEvent']>((event, handler, options?) => {
    privateApiRef.current?.eventManager.on(event, handler as EventListener, options);
    const api = privateApiRef.current;
    return () => {
      api.eventManager.removeListener(event, handler as EventListener);
    };
  }, []);

  useSwimlaneApiMethod(privateApiRef, { subscribeEvent, publishEvent }, 'public');

  if (inputApiRef && !inputApiRef.current?.instanceId) {
    inputApiRef.current = publicApiRef.current;
  }

  useImperativeHandle(inputApiRef, () => publicApiRef.current, []);

  useEffect(() => () => privateApiRef.current.publishEvent('unmount'), []);

  return privateApiRef;
}
